import { useState, useEffect } from 'react';
import { X, Download, Chrome, Sparkles } from 'lucide-react';
import { useTheme } from '../../zustand/ThemeStore';

export default function ExtensionBanner() {
    const [isVisible, setIsVisible] = useState(false);
    const isDarkMode = useTheme((state) => state.theme);

    useEffect(() => {
        // Check if banner was previously dismissed
        const dismissed = localStorage.getItem('extensionBannerDismissed');
        const dismissedTime = dismissed ? parseInt(dismissed) : 0;
        const now = Date.now();
        
        // Show banner again after 7 days (604800000 ms)
        if (!dismissed || (now - dismissedTime) > 604800000) {
            setIsVisible(true);
        }
    }, []);

    const handleDismiss = () => {
        setIsVisible(false);
        localStorage.setItem('extensionBannerDismissed', Date.now().toString());
    };

    const handleDownload = () => {
        // For now, link to Chrome Web Store or provide instructions
        // Update this URL when extension is published
        window.open('https://chromewebstore.google.com/detail/stride-sales-coach/mnhnmmkhlbdamppclfeekbklfbphbboc', '_blank');
    };

    if (!isVisible) return null;

    return (
        <div className={`relative overflow-hidden ${
            isDarkMode 
                ? 'bg-gradient-to-r from-blue-900 via-purple-900 to-blue-900' 
                : 'bg-gradient-to-r from-blue-500 via-purple-500 to-blue-600'
        }`}>
            {/* Animated background effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-shimmer"></div>
            
            <div className="relative max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-2">
                <div className="flex items-center justify-between gap-2 sm:gap-4">
                    {/* Icon with sparkle effect */}
                    <div className="relative flex-shrink-0">
                        <Chrome className="w-5 h-5 text-white" />
                        <Sparkles className="w-2.5 h-2.5 text-yellow-300 absolute -top-0.5 -right-0.5 animate-pulse" />
                    </div>
                    
                    {/* Message */}
                    <div className="flex-1 min-w-0">
                        <p className="text-white text-xs sm:text-sm font-medium flex items-center gap-2">
                            <span className="bg-yellow-400 text-gray-900 text-[10px] sm:text-xs font-bold px-1.5 py-0.5 rounded-full uppercase flex-shrink-0">
                                New
                            </span>
                            <span className="truncate">
                                Chrome Extension Launched! 🎉 - Analyze calls instantly
                            </span>
                        </p>
                    </div>
                    
                    {/* CTA Button */}
                    <button
                        onClick={handleDownload}
                        className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-semibold text-xs sm:text-sm transition-all duration-200 transform hover:scale-105 ${
                            isDarkMode
                                ? 'bg-white text-gray-900 hover:bg-gray-100'
                                : 'bg-gray-900 text-white hover:bg-gray-800'
                        } shadow-lg hover:shadow-xl`}
                    >
                        <Download className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                        <span>Download</span>
                    </button>
                    
                    {/* Close Button */}
                    <button
                        onClick={handleDismiss}
                        className="flex-shrink-0 p-0.5 rounded-full hover:bg-white/20 transition-colors duration-200"
                        aria-label="Dismiss banner"
                    >
                        <X className="w-4 h-4 text-white" />
                    </button>
                </div>
            </div>
        </div>
    );
}
